import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { candidatesApi, matchesApi, jobsApi, powerMatchesApi, revealApi, accessLogsApi, timelineApi, rolePermissionsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePageVisitLogger, logCandidateView, logClick } from '../hooks/useActivityLogger';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { DatePicker, TimePicker } from '../components/ui/date-picker';
import { StatusBadge, SkillTag, ScoreBadge } from '../components/common/Badges';
import ScoreRing from '../components/common/ScoreRing';
import ProfileCompletenessBar from '../components/common/ProfileCompletenessBar';
import { CircularTimer } from '../components/ui/circular-timer';
import { QRCodeSVG } from 'qrcode.react';
import AddToRequirementDialog from '../components/requirements/AddToRequirementDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  Loader2,
  ExternalLink,
  Calendar,
  DollarSign,
  Globe,
  Github,
  Linkedin,
  Target,
  Edit,
  Download,
  MessageSquarePlus,
  Trash2,
  StickyNote,
  Clock,
  User,
  CheckCircle,
  Bot,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  FileText,
  Zap,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Building2,
  QrCode,
  Smartphone,
  UserCheck,
  AlertTriangle,
  Copy,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import ScheduleInterviewDialog from '../components/interviews/ScheduleInterviewDialog'; // interviews-v2 (2026-09-07)
import CandidateInterviewCard from '../components/interviews/CandidateInterviewCard';
import QuickBgvMenuItem from '../components/quickbgv/QuickBgvMenuItem';
import JmArchivePanel from '../components/jmarchive/JmArchivePanel';
import BestFitPanel from '../components/matchview/BestFitPanel';
import AssessmentsCard from '../components/candidates/AssessmentsCard';
import VideoIntroCard from '../components/candidates/VideoIntroCard';


// Candidate Unlocks Tab for Admin
function CandidateUnlocksTab({ candidateId }) {
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/admin/candidate-unlocks/${candidateId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnlocks(data.unlocks || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    if (candidateId) load();
  }, [candidateId, API_URL]);

  if (loading) return <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Unlock History</h3>
          <Badge variant="secondary">{unlocks.length} unlocks</Badge>
        </div>
        {unlocks.length > 0 ? (
          <div className="space-y-3">
            {unlocks.map((u, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`unlock-item-${idx}`}>
                <div>
                  <p className="text-sm font-medium text-slate-700">{u.label || u.action}</p>
                  <p className="text-xs text-slate-400">{u.unlocked_at ? new Date(u.unlocked_at).toLocaleString() : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {u.jobs_triggered && <Badge className="text-[10px] bg-green-100 text-green-700">Jobs Triggered</Badge>}
                  {u.metadata?.source && <Badge variant="outline" className="text-[10px]">{u.metadata.source}</Badge>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No unlocks yet</p>
        )}
      </CardContent>
    </Card>
  );
}


export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Activity logging
  usePageVisitLogger('Candidate Detail');
  
  const [candidate, setCandidate] = useState(null);
  const [matches, setMatches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Resume Modal
  const [resumeOpen, setResumeOpen] = useState(false);
  const [originalCvUrl, setOriginalCvUrl] = useState(null);
  const [cvLoading, setCvLoading] = useState(false);
  
  // Helper function to safely display skill/item (handles both string and object types)
  const displayItem = (item) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) {
      return item.name || item.title || item.skill || JSON.stringify(item);
    }
    return String(item);
  };
  
  // Power Mode
  const [powerModeOpen, setPowerModeOpen] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState(['anthropic', 'openai', 'gemini']);
  const [powerLoading, setPowerLoading] = useState(false);
  const [powerResults, setPowerResults] = useState(null);
  const [jobMatchScores, setJobMatchScores] = useState({}); // Existing match scores per job
  
  // Power Parse
  const [powerParseOpen, setPowerParseOpen] = useState(false);
  const [parseProviders, setParseProviders] = useState(['anthropic', 'openai', 'gemini']);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseResults, setParseResults] = useState(null);
  const [selectedParseResult, setSelectedParseResult] = useState(null);
  const [applyingParse, setApplyingParse] = useState(false);
  const [selectedCustomFields, setSelectedCustomFields] = useState({});
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);

  // AI Summary Generation
  const [generatingAISummary, setGeneratingAISummary] = useState(false);

  // Access Control & Reveal
  const [permissions, setPermissions] = useState({});
  const [revealedFields, setRevealedFields] = useState({ phone: false, email: false, resume: false });
  const [revealedData, setRevealedData] = useState({ phone: null, email: null, resume: null });
  const [revealLoading, setRevealLoading] = useState({ phone: false, email: false, resume: false });
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [currentLogId, setCurrentLogId] = useState(null);
  const [currentField, setCurrentField] = useState(null);
  const [accessNotes, setAccessNotes] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Timeline
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [addTimelineOpen, setAddTimelineOpen] = useState(false);
  const [timelineEntry, setTimelineEntry] = useState({
    status: '',
    company_id: '',
    company_name: '',
    job_id: '',
    job_title: '',
    notes: ''
  });
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyForInterview, setSelectedCompanyForInterview] = useState(null);

  // Company search function for type-ahead
  const searchCompanies = async (query) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies?search=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      const data = await response.json();
      return data.companies || [];
    } catch (error) {
      console.error('Failed to search companies:', error);
      return [];
    }
  };

  // Power Match Data
  const [powerMatchData, setPowerMatchData] = useState(null);
  const [powerMatchLoading, setPowerMatchLoading] = useState(true);

  // Add to requirement (routes/requirement_links.py + AddToRequirementDialog)
  const [addToRequirementOpen, setAddToRequirementOpen] = useState(false);

  // Interview Scheduling
  const [scheduleInterviewOpen, setScheduleInterviewOpen] = useState(false);
  const [schedulingInterview, setSchedulingInterview] = useState(false);
  const [interviewData, setInterviewData] = useState({
    job_id: '',
    company_id: '',
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_time: '',
    location: '',
    attendee_emails: '',
    selected_attendees: [],
    send_notifications: true
  });
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(true);

  // Background Verification
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verifications, setVerifications] = useState([]);
  const [verificationsLoading, setVerificationsLoading] = useState(true);
  const [newVerification, setNewVerification] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_designation: '',
    candidate_designation: '',
    candidate_tenure_from: '',
    candidate_tenure_to: ''
  });
  const [sendingVerification, setSendingVerification] = useState(false);
  
  // Status change modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusCompanyId, setStatusCompanyId] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  // Cancel interview modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelInterviewId, setCancelInterviewId] = useState(null);
  const [cancelAction, setCancelAction] = useState('cancel'); // 'cancel' or 'reschedule'
  const [cancelReason, setCancelReason] = useState('');

  const TIMELINE_STATUSES = [
    { value: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-700' },
    { value: 'screened', label: 'Screened', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-purple-100 text-purple-700' },
    { value: 'interviewed', label: 'Interviewed', color: 'bg-violet-100 text-violet-700' },
    { value: 'shortlisted', label: 'Shortlisted', color: 'bg-amber-100 text-amber-700' },
    { value: 'offered', label: 'Offered', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'hired', label: 'Hired', color: 'bg-green-100 text-green-700' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
    { value: 'on_hold', label: 'On Hold', color: 'bg-slate-100 text-slate-700' },
  ];

  const CALL_STATUSES = [
    { value: 'connected', label: 'Connected', icon: PhoneCall, color: 'text-green-600' },
    { value: 'not_answered', label: 'Not Answered', icon: PhoneMissed, color: 'text-amber-600' },
    { value: 'busy', label: 'Busy', icon: PhoneOff, color: 'text-red-600' },
    { value: 'wrong_number', label: 'Wrong Number', icon: PhoneOff, color: 'text-red-600' },
    { value: 'voicemail', label: 'Left Voicemail', icon: Phone, color: 'text-blue-600' },
  ];

  useEffect(() => {
    loadCandidate();
    loadMatches();
    loadJobs();
    loadPermissions();
    loadTimeline();
    loadCompanies();
    loadPowerMatchData();
    loadInterviews();
    loadVerifications();
  }, [id]);

  const loadInterviews = async () => {
    try {
      setInterviewsLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/interviews?candidate_id=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInterviews(data.interviews || []);
      }
    } catch (error) {
      console.error('Failed to load interviews:', error);
    } finally {
      setInterviewsLoading(false);
    }
  };

  const loadVerifications = async () => {
    try {
      setVerificationsLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/background-verification?candidate_id=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVerifications(data.verifications || []);
      }
    } catch (error) {
      console.error('Failed to load verifications:', error);
    } finally {
      setVerificationsLoading(false);
    }
  };

  const handleAddVerification = async () => {
    if (!newVerification.company_name || !newVerification.contact_email) {
      toast.error('Company name and contact email are required');
      return;
    }

    setSendingVerification(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      // Step 1: Create the verification record
      const createResponse = await fetch(`${API_URL}/api/background-verification/create-company-contact`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: id,
          ...newVerification
        })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create verification');
      }

      const createData = await createResponse.json();

      // Step 2: Send the verification email with documents
      const sendResponse = await fetch(`${API_URL}/api/background-verification/${createData.verification_id}/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (sendResponse.ok) {
        toast.success(`Verification email sent to ${newVerification.contact_email}`);
        setVerificationModalOpen(false);
        setNewVerification({
          company_name: '',
          contact_name: '',
          contact_email: '',
          contact_phone: '',
          contact_designation: '',
          candidate_designation: '',
          candidate_tenure_from: '',
          candidate_tenure_to: ''
        });
        loadVerifications();
      } else {
        toast.warning('Verification created but email failed to send. Check SMTP settings.');
        loadVerifications();
      }
    } catch (error) {
      toast.error('Failed to create verification');
      console.error(error);
    } finally {
      setSendingVerification(false);
    }
  };


  const handleScheduleInterview = async () => {
    console.log('Interview data:', interviewData);
    if (!interviewData.title || !interviewData.start_date || !interviewData.start_time || !interviewData.end_time) {
      toast.error('Please fill in required fields: Title, Date, Start Time, End Time');
      return;
    }

    setSchedulingInterview(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');

      // Build ISO datetime strings WITHOUT 'Z' suffix
      // The backend applies org timezone (e.g. Asia/Kolkata) to interpret these as local time
      const startDateTime = `${interviewData.start_date}T${interviewData.start_time}:00`;
      const endDateTime = `${interviewData.start_date}T${interviewData.end_time}:00`;

      // Combine manual emails with selected company contact emails
      const manualEmails = interviewData.attendee_emails
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);
      const selectedEmails = interviewData.selected_attendees || [];
      const attendeeEmails = [...new Set([...manualEmails, ...selectedEmails])];

      const response = await fetch(`${API_URL}/api/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          candidate_id: id,
          job_id: interviewData.job_id || null,
          company_id: interviewData.company_id || null,
          title: interviewData.title,
          description: interviewData.description,
          start_time: startDateTime,
          end_time: endDateTime,
          location: interviewData.location,
          attendee_emails: attendeeEmails,
          send_notifications: interviewData.send_notifications
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Interview scheduled successfully!');
        if (data.google_calendar_link) {
          toast.success('Added to Google Calendar', {
            action: {
              label: 'View',
              onClick: () => window.open(data.google_calendar_link, '_blank')
            }
          });
        }
        setScheduleInterviewOpen(false);
        setInterviewData({
          job_id: '',
          company_id: '',
          title: '',
          description: '',
          start_date: '',
          start_time: '',
          end_time: '',
          location: '',
          attendee_emails: '',
          selected_attendees: [],
          send_notifications: true
        });
        loadInterviews();
        loadTimeline();
        loadCandidate();
      } else {
        toast.error(data.detail || 'Failed to schedule interview');
      }
    } catch (error) {
      toast.error('Failed to schedule interview');
    } finally {
      setSchedulingInterview(false);
    }
  };

  const handleCancelInterview = async (interviewId) => {
    // Open cancel modal instead of direct confirm
    setCancelInterviewId(interviewId);
    setCancelAction('cancel');
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const confirmCancelInterview = async () => {
    if (cancelAction === 'reschedule') {
      // Close cancel modal and open schedule modal with prefilled data
      setCancelModalOpen(false);
      const interviewToReschedule = interviews.find(i => i.id === cancelInterviewId);
      if (interviewToReschedule) {
        // Delete the old interview first
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const token = localStorage.getItem('ats_token');
          await fetch(`${API_URL}/api/interviews/${cancelInterviewId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          // Extract date and time from the original interview
          let startDate = '';
          let startTime = '';
          let endTime = '';
          
          if (interviewToReschedule.start_time) {
            const start = new Date(interviewToReschedule.start_time);
            startDate = start.toISOString().split('T')[0];
            startTime = start.toTimeString().slice(0, 5);
          }
          if (interviewToReschedule.end_time) {
            const end = new Date(interviewToReschedule.end_time);
            endTime = end.toTimeString().slice(0, 5);
          }
          
          // Pre-fill the schedule dialog with previous interview data
          setInterviewData({
            job_id: interviewToReschedule.job_id || '',
            company_id: interviewToReschedule.company_id || '',
            title: interviewToReschedule.title + ' (Rescheduled)',
            description: interviewToReschedule.description || '',
            start_date: startDate,
            start_time: startTime,
            end_time: endTime,
            location: interviewToReschedule.location || '',
            attendee_emails: interviewToReschedule.attendee_emails?.join(', ') || '',
            selected_attendees: [],
            send_notifications: true
          });
          setScheduleInterviewOpen(true);
          toast.info('Update the date and time for the rescheduled interview');
          loadInterviews();
          loadTimeline();
        } catch (error) {
          toast.error('Failed to reschedule interview');
        }
      }
      return;
    }

    // Cancel interview
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/interviews/${cancelInterviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Add timeline entry for cancellation
        await timelineApi.add(id, {
          status: 'interview_cancelled',
          notes: cancelReason || 'Interview cancelled'
        });
        toast.success('Interview cancelled');
        loadInterviews();
        loadTimeline();
      } else {
        toast.error('Failed to cancel interview');
      }
    } catch (error) {
      toast.error('Failed to cancel interview');
    }
    setCancelModalOpen(false);
  };

  const loadOriginalCv = async () => {
    if (!candidate?.original_file_path) return;
    
    setCvLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/candidates/${id}/original-cv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setOriginalCvUrl(url);
      }
    } catch (error) {
      console.error('Failed to load original CV:', error);
    } finally {
      setCvLoading(false);
    }
  };

  // Load CV when modal opens
  useEffect(() => {
    if (resumeOpen && candidate?.original_file_path && !originalCvUrl) {
      loadOriginalCv();
    }
  }, [resumeOpen, candidate?.original_file_path]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (originalCvUrl) {
        URL.revokeObjectURL(originalCvUrl);
      }
    };
  }, [originalCvUrl]);

  const loadPowerMatchData = async () => {
    try {
      setPowerMatchLoading(true);
      const response = await powerMatchesApi.getForCandidate(id);
      setPowerMatchData(response.data);
    } catch (error) {
      console.error('Failed to load power match data:', error);
    } finally {
      setPowerMatchLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await jobsApi.list({ limit: 100, status: 'open' });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to load jobs');
    }
  };

  const loadCandidate = async () => {
    try {
      const response = await candidatesApi.get(id);
      setCandidate(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load candidate');
      navigate('/ats/candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    try {
      const response = await matchesApi.list({ candidate_id: id, limit: 10 });
      setMatches(response.data.matches);
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await rolePermissionsApi.getAll();
      const userRole = user?.role || 'recruiter';
      setPermissions(response.data[userRole] || {});
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await timelineApi.get(id);
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Failed to load timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies?limit=100`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  // Reveal sensitive field
  const handleReveal = async (field) => {
    setRevealLoading(prev => ({ ...prev, [field]: true }));
    try {
      const response = await revealApi.reveal(id, field);
      setRevealedData(prev => ({ ...prev, [field]: response.data.value }));
      setRevealedFields(prev => ({ ...prev, [field]: true }));
      
      // Check if notes are required
      if (response.data.require_notes || permissions.require_access_notes) {
        setCurrentLogId(response.data.log_id);
        setCurrentField(field);
        setAccessNotes('');
        setCallStatus('');
        setNotesModalOpen(true);
      } else {
        toast.success(`${field} revealed and logged`);
      }
    } catch (error) {
      toast.error(`Failed to reveal ${field}`);
    } finally {
      setRevealLoading(prev => ({ ...prev, [field]: false }));
    }
  };

  // Save access notes
  const handleSaveAccessNotes = async () => {
    if (!currentLogId) {
      console.error('No currentLogId set');
      toast.error('Error: No log ID available');
      return;
    }
    
    setSavingNotes(true);
    try {
      await accessLogsApi.addNotes(currentLogId, accessNotes, callStatus);
      toast.success('Notes saved');
      setNotesModalOpen(false);
      setCurrentLogId(null);
      setCurrentField(null);
    } catch (error) {
      console.error('Failed to save notes:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Add timeline entry
  const handleAddTimelineEntry = async () => {
    if (!timelineEntry.status) {
      toast.error('Please select a status');
      return;
    }
    
    try {
      await timelineApi.add(id, timelineEntry);
      toast.success('Timeline entry added');
      setAddTimelineOpen(false);
      setTimelineEntry({ status: '', company_id: '', company_name: '', job_id: '', job_title: '', notes: '' });
      loadTimeline();
      loadCandidate(); // Refresh to get updated status
    } catch (error) {
      toast.error('Failed to add timeline entry');
    }
  };

  // Check if field should be hidden
  const shouldHideField = (field) => {
    const permKey = `view_candidate_${field}`;
    return !permissions[permKey] && permissions.require_click_to_reveal;
  };

  const handleStatusChange = async (newStatus) => {
    // Open modal for status change with company and notes
    setPendingStatus(newStatus);
    setStatusCompanyId('');
    setStatusNote('');
    setStatusModalOpen(true);
  };

  const confirmStatusChange = async () => {
    setSavingStatus(true);
    try {
      await candidatesApi.update(id, { status: pendingStatus });
      
      // Add timeline entry with company and note
      const timelineEntry = {
        status: pendingStatus,
        company_id: statusCompanyId || null,
        company_name: statusCompanyId ? companies.find(c => c.id === statusCompanyId)?.company_name : null,
        notes: statusNote || `Status changed to ${pendingStatus}`
      };
      await timelineApi.add(id, timelineEntry);
      
      setCandidate({ ...candidate, status: pendingStatus });
      setStatusModalOpen(false);
      loadTimeline();
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setAddingNote(true);
    try {
      const response = await candidatesApi.addNote(id, newNote.trim());
      setCandidate(response.data);
      setNewNote('');
      toast.success('Note added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      const response = await candidatesApi.deleteNote(id, noteId);
      setCandidate(response.data);
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSkillsChange = (field, value) => {
    const skills = value.split(',').map(s => s.trim()).filter(s => s);
    setEditData(prev => ({ ...prev, [field]: skills }));
  };

  const handleVerifyAndSave = async () => {
    setSaving(true);
    try {
      const response = await candidatesApi.verify(id, editData);
      setCandidate(response.data);
      setEditData(response.data);
      setIsEditing(false);
      toast.success('Candidate verified successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickVerify = async () => {
    setSaving(true);
    try {
      const response = await candidatesApi.verify(id, {});
      setCandidate(response.data);
      toast.success('Candidate approved');
    } catch (error) {
      toast.error('Failed to verify candidate');
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditData(candidate);
    setIsEditing(false);
  };

  const toggleProvider = (provider) => {
    setSelectedProviders(prev =>
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  // Load existing match scores for all jobs when Power Match modal opens
  const loadJobMatchScores = async () => {
    try {
      const response = await matchesApi.list({ candidate_id: id, limit: 100 });
      const matches = response.data.matches || [];
      const scores = {};
      matches.forEach(match => {
        if (match.job_id && match.overall_score) {
          // Keep the highest score if multiple matches exist for same job
          if (!scores[match.job_id] || match.overall_score > scores[match.job_id]) {
            scores[match.job_id] = match.overall_score;
          }
        }
      });
      setJobMatchScores(scores);
    } catch (error) {
      console.error('Failed to load job match scores:', error);
    }
  };

  // Load scores when modal opens
  useEffect(() => {
    if (powerModeOpen) {
      loadJobMatchScores();
    }
  }, [powerModeOpen]);

  const handlePowerMatch = async () => {
    if (selectedJobIds.length === 0) {
      toast.error('Please select at least one job');
      return;
    }
    if (selectedProviders.length === 0) {
      toast.error('Please select at least one AI provider');
      return;
    }

    setPowerLoading(true);
    setPowerResults(null);
    
    try {
      // Run Power Match for all selected jobs
      const allResults = [];
      
      for (const jobId of selectedJobIds) {
        try {
          const response = await candidatesApi.powerMatch(id, jobId, selectedProviders);
          allResults.push(response.data);
        } catch (error) {
          console.error(`Power Match failed for job ${jobId}:`, error);
        }
      }
      
      if (allResults.length === 0) {
        toast.error('Power Match failed for all selected jobs');
        return;
      }
      
      // If single job, use original format
      if (allResults.length === 1) {
        setPowerResults(allResults[0]);
      } else {
        // Multiple jobs - create combined result
        setPowerResults({
          multi_job: true,
          results: allResults,
          total_jobs: allResults.length,
          candidate_id: id,
          candidate_name: candidate?.name
        });
      }
      
      toast.success(`Power Match completed for ${allResults.length} job(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Power Match failed');
    } finally {
      setPowerLoading(false);
    }
  };

  const handleSavePowerMatch = async (resultToSave = null) => {
    const result = resultToSave || powerResults;
    if (!result || result.multi_job) return;
    
    try {
      await powerMatchesApi.save({
        candidate_id: result.candidate_id,
        candidate_name: result.candidate_name,
        job_id: result.job_id,
        job_title: result.job_title,
        provider_results: result.results,
        average_score: result.average_score,
        best_provider: result.best_provider,
        best_score: result.best_score,
      });
      toast.success('Power Match saved successfully!');
      if (!resultToSave) {
        setPowerModeOpen(false);
        setPowerResults(null);
      }
      // Refresh Power Match data to show updated scores
      loadPowerMatchData();
      loadMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Power Match');
    }
  };

  const handleSaveAllPowerMatches = async () => {
    if (!powerResults?.multi_job) return;
    
    let savedCount = 0;
    for (const result of powerResults.results) {
      try {
        await powerMatchesApi.save({
          candidate_id: result.candidate_id,
          candidate_name: result.candidate_name,
          job_id: result.job_id,
          job_title: result.job_title,
          provider_results: result.results,
          average_score: result.average_score,
          best_provider: result.best_provider,
          best_score: result.best_score,
        });
        savedCount++;
      } catch (error) {
        console.error(`Failed to save match for job ${result.job_id}:`, error);
      }
    }
    
    toast.success(`Saved ${savedCount} of ${powerResults.results.length} matches`);
    setPowerModeOpen(false);
    setPowerResults(null);
    loadPowerMatchData();
    loadMatches();
  };

  // Power Parse handlers
  const toggleParseProvider = (provider) => {
    setParseProviders(prev =>
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  const handlePowerParse = async () => {
    if (parseProviders.length === 0) {
      toast.error('Select at least one AI provider');
      return;
    }
    
    setParseLoading(true);
    setParseResults(null);
    setSelectedParseResult(null);
    try {
      const response = await candidatesApi.powerParse(id, parseProviders);
      setParseResults(response.data);
      toast.success(`Power Parse completed with ${Object.keys(response.data.results).length} AI(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Power Parse failed');
    } finally {
      setParseLoading(false);
    }
  };

  const handleApplyPowerParse = async () => {
    if (!selectedParseResult) {
      toast.error('Please select a parsing result to apply');
      return;
    }
    
    // Check if there are custom fields to review
    const customFields = selectedParseResult.customFields || {};
    if (Object.keys(customFields).length > 0 && Object.keys(selectedCustomFields).length === 0) {
      // First time - show custom fields selection
      setSelectedCustomFields(customFields);
      setShowCustomFieldsModal(true);
      return;
    }
    
    setApplyingParse(true);
    try {
      await candidatesApi.applyPowerParse(
        id, 
        selectedParseResult.provider, 
        selectedParseResult.data,
        selectedCustomFields,
        true
      );
      toast.success(`Applied ${selectedParseResult.provider} parsing result!`);
      setPowerParseOpen(false);
      setParseResults(null);
      setSelectedParseResult(null);
      setSelectedCustomFields({});
      setShowCustomFieldsModal(false);
      loadCandidate(); // Refresh candidate data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply parsing result');
    } finally {
      setApplyingParse(false);
    }
  };

  const handleGenerateAISummary = async () => {
    setGeneratingAISummary(true);
    try {
      const response = await candidatesApi.generateAISummary(id);
      toast.success('AI summary and CTC estimation generated!');
      loadCandidate(); // Refresh candidate data
      loadTimeline(); // Refresh timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI summary');
    } finally {
      setGeneratingAISummary(false);
    }
  };

  const toggleCustomField = (fieldName, value) => {
    setSelectedCustomFields(prev => {
      const updated = { ...prev };
      if (updated[fieldName] !== undefined) {
        delete updated[fieldName];
      } else {
        updated[fieldName] = value;
      }
      return updated;
    });
  };

  const formatNoteDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!candidate) return null;

  const isUnverified = candidate.verification_status !== 'verified';

  return (
    <div>
      <Header 
        title={candidate.name} 
        subtitle={candidate.current_designation || candidate.candidate_code}
      >
        <Button variant="outline" onClick={() => navigate('/ats/candidates')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {candidate.cv_text && (
          shouldHideField('resume') && !revealedFields.resume ? (
            <Button 
              variant="outline" 
              onClick={() => handleReveal('resume')}
              disabled={revealLoading.resume}
              data-testid="reveal-resume-btn"
            >
              {revealLoading.resume ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Unlock Resume
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setResumeOpen(true)} data-testid="view-resume-btn">
              <FileText className="w-4 h-4 mr-2" />
              View Resume
              {revealedFields.resume && <Eye className="w-3 h-3 ml-1 text-green-500" />}
            </Button>
          )
        )}
        
        {/* AI Tools Dropdown - Power Parse, AI Summary, Power Match */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-0 hover:from-purple-600 hover:to-indigo-600"
              data-testid="ai-tools-dropdown"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              AI Tools
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem 
              onClick={() => setPowerParseOpen(true)}
              className="cursor-pointer"
              data-testid="power-parse-btn"
            >
              <Bot className="w-4 h-4 mr-2 text-amber-500" />
              Power Parse
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleGenerateAISummary}
              disabled={generatingAISummary}
              className="cursor-pointer"
              data-testid="generate-ai-summary-btn"
            >
              {generatingAISummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-emerald-500" />}
              {generatingAISummary ? 'Generating...' : 'AI Summary'}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setPowerModeOpen(true)}
              className="cursor-pointer"
              data-testid="power-match-btn"
            >
              <Zap className="w-4 h-4 mr-2 text-blue-500" />
              Power Match
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Actions Dropdown - Schedule Interview, Background Verify */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="bg-gradient-to-r from-green-500 to-teal-500 text-white border-0 hover:from-green-600 hover:to-teal-600"
              data-testid="actions-dropdown"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Actions
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem 
              onClick={() => { logClick('Schedule Interview', candidate?.name); setScheduleInterviewOpen(true); }}
              className="cursor-pointer"
              data-testid="schedule-interview-btn"
            >
              <Calendar className="w-4 h-4 mr-2 text-green-500" />
              Schedule Interview
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => { logClick('Background Verify', candidate?.name); setVerificationModalOpen(true); }}
              className="cursor-pointer"
              data-testid="verify-background-btn"
            >
              <UserCheck className="w-4 h-4 mr-2 text-violet-500" />
              Background Verify
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { logClick('Add to Requirement', candidate?.name); setAddToRequirementOpen(true); }}
              className="cursor-pointer"
              data-testid="add-to-requirement-btn"
            >
              <Briefcase className="w-4 h-4 mr-2 text-emerald-500" />
              Add to Requirement
            </DropdownMenuItem>
            {/* Quick BGV — takes this candidate straight into the BGV module,
                pre-filled. Nothing is emailed to any employer. */}
            <QuickBgvMenuItem as={DropdownMenuItem} className="cursor-pointer" candidate={candidate} />
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Add this candidate to one or many requirements, then offer the dossier page */}
        <AddToRequirementDialog
          open={addToRequirementOpen}
          onOpenChange={setAddToRequirementOpen}
          mode="candidate"
          candidateIds={candidate?.id ? [candidate.id] : []}
          candidateNames={candidate?.name ? [candidate.name] : []}
        />

        {isEditing ? (
          <>
            <Button variant="outline" onClick={cancelEditing} disabled={saving}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleVerifyAndSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-verify-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save & Verify
            </Button>
          </>
        ) : (
          <>
            {isUnverified && (
              <Button variant="outline" onClick={handleQuickVerify} disabled={saving} data-testid="quick-approve-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-btn">
              <Edit className="w-4 h-4 mr-2" />
              {isUnverified ? 'Edit & Verify' : 'Edit'}
            </Button>
            <Select value={candidate.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-36" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="screening">Screening</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="offered">Offered</SelectItem>
                <SelectItem value="hired">Hired</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </Header>

      <div className="page-container">
        {/* Verification Banner */}
        {isUnverified && !isEditing && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="verification-banner">
            <div className="flex items-center gap-3">
              <Bot className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">AI-Parsed Data</p>
                <p className="text-sm text-amber-600">Review and verify this candidate&apos;s information for accuracy</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setIsEditing(true)} className="bg-amber-600 hover:bg-amber-700">
              Review & Edit
            </Button>
          </div>
        )}

        {/* Interviews (v2) - reads /api/interviews-v2, owns schedule/reschedule/outcome/cancel */}
        {candidate && (
          <CandidateInterviewCard candidate={candidate} onChanged={() => { loadInterviews(); loadTimeline(); loadCandidate(); }} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Completeness Bar */}
            {!isEditing && (
              <ProfileCompletenessBar candidateId={id} data-testid="profile-completeness-bar" />
            )}
            
            {/* Profile Summary */}
            <Card data-testid="profile-card">
              <CardContent className="p-6">
                {isEditing ? (
                  /* Editing Mode */
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 mb-4">Edit Profile Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input id="name" value={editData.name || ''} onChange={(e) => handleEditChange('name', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={editData.email || ''} onChange={(e) => handleEditChange('email', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" value={editData.phone || ''} onChange={(e) => handleEditChange('phone', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="current_location">Location</Label>
                        <Input id="current_location" value={editData.current_location || ''} onChange={(e) => handleEditChange('current_location', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="current_designation">Current Role</Label>
                        <Input id="current_designation" value={editData.current_designation || ''} onChange={(e) => handleEditChange('current_designation', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="current_company">Current Company</Label>
                        <Input id="current_company" value={editData.current_company || ''} onChange={(e) => handleEditChange('current_company', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="total_years_experience">Years of Experience</Label>
                        <Input id="total_years_experience" type="number" value={editData.total_years_experience || ''} onChange={(e) => handleEditChange('total_years_experience', parseInt(e.target.value) || 0)} />
                      </div>
                      <div>
                        <Label htmlFor="notice_period_days">Notice Period (days)</Label>
                        <Input id="notice_period_days" value={editData.notice_period_days || ''} onChange={(e) => handleEditChange('notice_period_days', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="current_salary">Current Salary</Label>
                        <Input id="current_salary" value={editData.current_salary || ''} onChange={(e) => handleEditChange('current_salary', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="expected_salary">Expected Salary</Label>
                        <Input id="expected_salary" value={editData.expected_salary || ''} onChange={(e) => handleEditChange('expected_salary', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="professional_summary">Professional Summary</Label>
                      <Textarea id="professional_summary" rows={3} value={editData.professional_summary || ''} onChange={(e) => handleEditChange('professional_summary', e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="technical_skills">Technical Skills (comma-separated)</Label>
                      <Input id="technical_skills" value={(editData.technical_skills || []).join(', ')} onChange={(e) => handleSkillsChange('technical_skills', e.target.value)} placeholder="React, Node.js, Python..." />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                        <Input id="linkedin_url" value={editData.linkedin_url || ''} onChange={(e) => handleEditChange('linkedin_url', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="github_url">GitHub URL</Label>
                        <Input id="github_url" value={editData.github_url || ''} onChange={(e) => handleEditChange('github_url', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-slate-600">
                      {candidate.name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold text-slate-900">{candidate.name}</h2>
                          {candidate.verification_status === 'verified' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" title="Verified" />
                          ) : (
                            <Bot className="w-5 h-5 text-amber-500" title="AI Parsed" />
                          )}
                        </div>
                        <p className="text-slate-600">{candidate.current_designation}</p>
                        {candidate.current_company && (
                          <p className="text-sm text-slate-500">at {candidate.current_company}</p>
                        )}
                        {/* AI Estimated CTC - shown prominently next to company */}
                        {candidate.estimated_ctc && (
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                              <Bot className="w-3 h-3 mr-1" />
                              Est. CTC: {candidate.estimated_ctc}
                            </Badge>
                          </div>
                        )}
                        {/* Background Verification Status Summary */}
                        {verifications.length > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-slate-500">BGV:</span>
                            {verifications.filter(v => v.status === 'verified').length > 0 && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {verifications.filter(v => v.status === 'verified').length} Verified
                              </Badge>
                            )}
                            {verifications.filter(v => v.status === 'pending' || v.status === 'email_sent').length > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                {verifications.filter(v => v.status === 'pending' || v.status === 'email_sent').length} Pending
                              </Badge>
                            )}
                            {verifications.filter(v => v.status === 'on_hold').length > 0 && (
                              <Badge className="bg-slate-100 text-slate-700 text-xs">
                                {verifications.filter(v => v.status === 'on_hold').length} On Hold
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <StatusBadge status={candidate.status} />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                      {/* Email - Click to Reveal */}
                      {candidate.email && (
                        shouldHideField('email') && !revealedFields.email ? (
                          <button
                            onClick={() => handleReveal('email')}
                            disabled={revealLoading.email}
                            className="flex items-center gap-2 text-slate-400 hover:text-blue-600 bg-slate-50 px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-blue-400 transition-colors"
                            data-testid="reveal-email-btn"
                          >
                            {revealLoading.email ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Lock className="w-4 h-4" />
                            )}
                            <span>Click to reveal email</span>
                          </button>
                        ) : (
                          <a href={`mailto:${revealedData.email || candidate.email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{revealedData.email || candidate.email}</span>
                            {revealedFields.email && <Eye className="w-3 h-3 text-green-500" />}
                          </a>
                        )
                      )}
                      
                      {/* Phone - Click to Reveal */}
                      {candidate.phone && (
                        shouldHideField('phone') && !revealedFields.phone ? (
                          <button
                            onClick={() => handleReveal('phone')}
                            disabled={revealLoading.phone}
                            className="flex items-center gap-2 text-slate-400 hover:text-blue-600 bg-slate-50 px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-blue-400 transition-colors"
                            data-testid="reveal-phone-btn"
                          >
                            {revealLoading.phone ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Lock className="w-4 h-4" />
                            )}
                            <span>Click to reveal phone</span>
                          </button>
                        ) : (
                          <a href={`tel:${revealedData.phone || candidate.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
                            <Phone className="w-4 h-4" />
                            {revealedData.phone || candidate.phone}
                            {revealedFields.phone && <Eye className="w-3 h-3 text-green-500" />}
                          </a>
                        )
                      )}
                      
                      {candidate.current_location && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin className="w-4 h-4" />
                          {candidate.current_location}
                        </div>
                      )}
                      {candidate.total_years_experience !== null && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Briefcase className="w-4 h-4" />
                          {candidate.total_years_experience} years exp
                        </div>
                      )}
                    </div>

                    {/* Social Links */}
                    <div className="flex gap-2 mt-4">
                      {candidate.linkedin_url && (
                        <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-blue-600 bg-slate-100 rounded-lg">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {candidate.github_url && (
                        <a href={candidate.github_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-slate-900 bg-slate-100 rounded-lg">
                          <Github className="w-4 h-4" />
                        </a>
                      )}
                      {candidate.portfolio_url && (
                        <a href={candidate.portfolio_url} target="_blank" rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-blue-600 bg-slate-100 rounded-lg">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    
                    {/* Candidate Portal Link */}
                    {candidate.portal_slug && (
                      <div className="mt-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                        <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-1">Candidate Portal</p>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded border flex-1 truncate">
                            {window.location.origin}/c/{candidate.portal_slug}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/c/${candidate.portal_slug}`);
                              toast.success('Portal URL copied!');
                            }}
                            className="p-1.5 text-violet-600 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded"
                            title="Copy URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={`/c/${candidate.portal_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-violet-600 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded"
                            title="Open Portal"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {candidate.professional_summary && (
                  <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Summary</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{candidate.professional_summary}</p>
                  </div>
                )}

                {/* Quick Stats Cards */}
                <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {candidate.current_industry && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Industry</p>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{candidate.current_industry}</p>
                      </div>
                    )}
                    {candidate.career_level && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Career Level</p>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 capitalize">{candidate.career_level}</p>
                      </div>
                    )}
                    {(candidate.notice_period_days || candidate.notice_period) && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Notice Period</p>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{candidate.notice_period || `${candidate.notice_period_days} days`}</p>
                      </div>
                    )}
                    {(candidate.current_salary || candidate.expected_salary) && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Salary</p>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {candidate.expected_salary ? `Exp: ${candidate.expected_salary}` : `Curr: ${candidate.current_salary}`}
                        </p>
                      </div>
                    )}
                    {candidate.estimated_ctc && (
                      <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-3">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <Bot className="w-3 h-3" />
                          AI Estimated CTC
                        </p>
                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{candidate.estimated_ctc}</p>
                      </div>
                    )}
                    {candidate.cloud_platforms?.length > 0 && (
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                        <p className="text-xs text-blue-600 dark:text-blue-400">Cloud Platforms</p>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200 truncate">{candidate.cloud_platforms.join(', ')}</p>
                      </div>
                    )}
                    {(candidate.languages_spoken?.length > 0 || candidate.language_proficiency?.length > 0) && (
                      <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                        <p className="text-xs text-purple-600 dark:text-purple-400">Languages</p>
                        <p className="text-sm font-medium text-purple-800 dark:text-purple-200 truncate">
                          {(candidate.languages_spoken || candidate.language_proficiency || []).map(lang => displayItem(lang)).join(', ')}
                        </p>
                      </div>
                    )}
                    {candidate.awards_recognition?.length > 0 && (
                      <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3">
                        <p className="text-xs text-amber-600 dark:text-amber-400">Awards</p>
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{candidate.awards_recognition.length} Recognition{candidate.awards_recognition.length !== 1 ? 's' : ''}</p>
                      </div>
                    )}
                    {candidate.available_from_date && (
                      <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                        <p className="text-xs text-green-600 dark:text-green-400">Available From</p>
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">{new Date(candidate.available_from_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tabs */}
            <BestFitPanel candidateId={candidate?.id || id} />

            <JmArchivePanel candidateId={candidate?.id || id} />

            <Tabs defaultValue="experience" className="w-full">
              <TabsList className="w-full justify-start flex-wrap" data-testid="detail-tabs">
                <TabsTrigger value="experience">Experience</TabsTrigger>
                <TabsTrigger value="education">Education</TabsTrigger>
                <TabsTrigger value="skills">Skills</TabsTrigger>
                <TabsTrigger value="certifications">
                  Certifications {candidate.certifications?.length > 0 && `(${candidate.certifications.length})`}
                </TabsTrigger>
                {(candidate.awards_recognition?.length > 0 || candidate.publications?.length > 0 || candidate.projects?.length > 0) && (
                  <TabsTrigger value="achievements">
                    <Award className="w-3.5 h-3.5 mr-1.5 text-amber-500" />
                    Achievements
                  </TabsTrigger>
                )}
                <TabsTrigger value="documents">
                  <FileText className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                  Documents {candidate.documents?.length > 0 && `(${candidate.documents.length})`}
                </TabsTrigger>
                <TabsTrigger value="timeline">
                  Timeline {timeline?.length > 0 && `(${timeline.length})`}
                </TabsTrigger>
                <TabsTrigger value="notes">
                  Notes {candidate.notes_history?.length > 0 && `(${candidate.notes_history.length})`}
                </TabsTrigger>
                <TabsTrigger value="verifications">
                  Verifications {verifications?.length > 0 && `(${verifications.length})`}
                </TabsTrigger>
                <TabsTrigger value="unlocks" data-testid="unlocks-tab">
                  Unlocks
                </TabsTrigger>
                {candidate.custom_fields && Object.keys(candidate.custom_fields).length > 0 && (
                  <TabsTrigger value="custom_fields">
                    <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-500" />
                    AI Extracted ({Object.keys(candidate.custom_fields).length})
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="experience" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    {candidate.work_experience?.length > 0 ? (
                      <div className="timeline">
                        {candidate.work_experience.map((exp, i) => (
                          <div key={i} className="timeline-item">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-slate-900">{exp.position}</h4>
                                <p className="text-sm text-slate-600">{exp.company_name}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {exp.start_date} - {exp.end_date || 'Present'}
                                  {exp.location && ` • ${exp.location}`}
                                </p>
                              </div>
                              {exp.employment_type && (
                                <span className="badge badge-neutral">{exp.employment_type}</span>
                              )}
                            </div>
                            {exp.responsibilities && (
                              <p className="text-sm text-slate-600 mt-2">{exp.responsibilities}</p>
                            )}
                            {exp.technologies_used?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {exp.technologies_used.map((tech, j) => (
                                  <span key={j} className="skill-tag">{tech}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No experience data</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="education" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    {candidate.education?.length > 0 ? (
                      <div className="space-y-4">
                        {candidate.education.map((edu, i) => (
                          <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                            <GraduationCap className="w-5 h-5 text-slate-400 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-slate-900">{edu.degree_type} in {edu.field_of_study}</h4>
                              <p className="text-sm text-slate-600">{edu.institution_name}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {edu.graduation_year}
                                {edu.percentage_cgpa && ` • ${edu.percentage_cgpa}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No education data</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="skills" className="mt-4">
                <Card>
                  <CardContent className="p-6 space-y-4">
                    {candidate.technical_skills?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Technical Skills</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.technical_skills.map((skill, i) => (
                            <span key={i} className="skill-tag">{displayItem(skill)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.programming_languages?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Programming Languages</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.programming_languages.map((lang, i) => (
                            <span key={i} className="skill-tag bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">{displayItem(lang)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.frameworks_libraries?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Frameworks & Libraries</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.frameworks_libraries.map((fw, i) => (
                            <span key={i} className="skill-tag bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">{displayItem(fw)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.tools_technologies?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Tools & Technologies</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.tools_technologies.map((tool, i) => (
                            <span key={i} className="skill-tag bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">{displayItem(tool)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.cloud_platforms?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Cloud Platforms</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.cloud_platforms.map((cloud, i) => (
                            <span key={i} className="skill-tag bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300">{displayItem(cloud)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.databases?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Databases</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.databases.map((db, i) => (
                            <span key={i} className="skill-tag bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">{displayItem(db)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.soft_skills?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Soft Skills</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.soft_skills.map((skill, i) => (
                            <span key={i} className="skill-tag bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300">{displayItem(skill)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.domain_knowledge?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Domain Knowledge</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.domain_knowledge.map((domain, i) => (
                            <span key={i} className="skill-tag bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">{displayItem(domain)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {candidate.methodologies?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Methodologies</h4>
                        <div className="flex flex-wrap gap-1">
                          {candidate.methodologies.map((method, i) => (
                            <span key={i} className="skill-tag bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{displayItem(method)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Show empty state if no skills */}
                    {!candidate.technical_skills?.length && !candidate.programming_languages?.length && 
                     !candidate.frameworks_libraries?.length && !candidate.soft_skills?.length && (
                      <p className="text-sm text-slate-500 text-center py-8">No skills data parsed</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="certifications" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    {candidate.certifications?.length > 0 ? (
                      <div className="space-y-3">
                        {candidate.certifications.map((cert, i) => {
                          // Handle both string and object certifications
                          const certName = typeof cert === 'string' ? cert : (cert.certification_name || cert.name || 'Unknown Certification');
                          const certOrg = typeof cert === 'object' ? (cert.issuing_organization || cert.issuer || '') : '';
                          const certDate = typeof cert === 'object' ? (cert.issue_date || cert.date || '') : '';
                          const certExpiry = typeof cert === 'object' ? cert.expiry_date : '';
                          
                          return (
                            <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                              <Award className="w-5 h-5 text-amber-500 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-slate-900 dark:text-slate-100">{certName}</h4>
                                {certOrg && <p className="text-sm text-slate-600 dark:text-slate-400">{certOrg}</p>}
                                {certDate && (
                                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                                    {certDate}
                                    {certExpiry && ` - ${certExpiry}`}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No certifications</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Achievements Tab - Awards, Publications, Projects */}
              <TabsContent value="achievements" className="mt-4">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    {/* Awards & Recognition */}
                    {candidate.awards_recognition?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                          <Award className="w-4 h-4 text-amber-500" />
                          Awards & Recognition
                        </h4>
                        <div className="space-y-3">
                          {candidate.awards_recognition.map((award, i) => (
                            <div key={i} className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800">
                              <Award className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <h5 className="font-medium text-slate-900 dark:text-slate-100">
                                  {typeof award === 'string' ? award : award.title || award.name}
                                </h5>
                                {typeof award === 'object' && (
                                  <>
                                    {award.organization && <p className="text-sm text-slate-600 dark:text-slate-400">{award.organization}</p>}
                                    {award.year && <p className="text-xs text-slate-500 mt-1">{award.year}</p>}
                                    {award.description && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{award.description}</p>}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Publications */}
                    {candidate.publications?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          Publications
                        </h4>
                        <div className="space-y-3">
                          {candidate.publications.map((pub, i) => (
                            <div key={i} className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                              <FileText className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <h5 className="font-medium text-slate-900 dark:text-slate-100">
                                  {typeof pub === 'string' ? pub : pub.title}
                                </h5>
                                {typeof pub === 'object' && (
                                  <>
                                    {pub.journal && <p className="text-sm text-slate-600 dark:text-slate-400">{pub.journal}</p>}
                                    {pub.year && <p className="text-xs text-slate-500 mt-1">{pub.year}</p>}
                                    {pub.url && (
                                      <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1">
                                        View Publication <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Projects */}
                    {candidate.projects?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-purple-500" />
                          Notable Projects
                        </h4>
                        <div className="space-y-3">
                          {candidate.projects.map((project, i) => (
                            <div key={i} className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
                              <h5 className="font-medium text-slate-900 dark:text-slate-100">
                                {typeof project === 'string' ? project : project.name || project.title}
                              </h5>
                              {typeof project === 'object' && (
                                <>
                                  {project.description && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{project.description}</p>}
                                  {project.technologies?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {project.technologies.map((tech, j) => (
                                        <span key={j} className="skill-tag text-xs">{tech}</span>
                                      ))}
                                    </div>
                                  )}
                                  {project.url && (
                                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline mt-2 inline-flex items-center gap-1">
                                      View Project <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {!candidate.awards_recognition?.length && !candidate.publications?.length && !candidate.projects?.length && (
                      <p className="text-sm text-slate-500 text-center py-8">No achievements data parsed</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-4">
                <div className="space-y-4 mb-4" data-testid="a7-cards">
                  <AssessmentsCard candidateId={candidate.id} />
                  <VideoIntroCard candidateId={candidate.id} />
                </div>
                <Card data-testid="documents-card">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Candidate Documents</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">ID proofs, certificates, experience letters, and other documents</p>
                      </div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;
                            
                            for (let i = 0; i < files.length; i++) {
                              const file = files[i];
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('document_name', file.name.replace(/\.[^/.]+$/, ''));
                              formData.append('document_type', 'other');
                              
                              try {
                                const API_URL = process.env.REACT_APP_BACKEND_URL;
                                const token = localStorage.getItem('ats_token');
                                const response = await fetch(`${API_URL}/api/candidates/${id}/documents`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                if (response.ok) {
                                  toast.success(`Uploaded: ${file.name}`);
                                  loadCandidate();
                                } else {
                                  toast.error(`Failed to upload: ${file.name}`);
                                }
                              } catch (error) {
                                toast.error(`Failed to upload: ${file.name}`);
                              }
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button asChild>
                          <span>
                            <FileText className="w-4 h-4 mr-2" />
                            Upload Documents
                          </span>
                        </Button>
                      </label>
                    </div>

                    {candidate.documents?.length > 0 ? (
                      <div className="space-y-3">
                        {candidate.documents.map((doc) => (
                          <div 
                            key={doc.id} 
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{doc.name}</p>
                                <p className="text-xs text-slate-500">
                                  {doc.type} • Uploaded by {doc.uploaded_by} • {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const newName = prompt('Enter new document name:', doc.name);
                                  if (newName && newName !== doc.name) {
                                    try {
                                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                                      const token = localStorage.getItem('ats_token');
                                      const response = await fetch(
                                        `${API_URL}/api/candidates/${id}/documents/${doc.id}?new_name=${encodeURIComponent(newName)}`,
                                        {
                                          method: 'PUT',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        }
                                      );
                                      if (response.ok) {
                                        toast.success('Document renamed');
                                        loadCandidate();
                                      }
                                    } catch (error) {
                                      toast.error('Failed to rename document');
                                    }
                                  }
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                                    const token = localStorage.getItem('ats_token');
                                    const response = await fetch(
                                      `${API_URL}/api/candidates/${id}/documents/${doc.id}/download`,
                                      { headers: { 'Authorization': `Bearer ${token}` } }
                                    );
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = doc.original_filename || doc.name;
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                    }
                                  } catch (error) {
                                    toast.error('Failed to download document');
                                  }
                                }}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={async () => {
                                  if (!confirm('Delete this document?')) return;
                                  try {
                                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                                    const token = localStorage.getItem('ats_token');
                                    const response = await fetch(
                                      `${API_URL}/api/candidates/${id}/documents/${doc.id}`,
                                      {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      }
                                    );
                                    if (response.ok) {
                                      toast.success('Document deleted');
                                      loadCandidate();
                                    }
                                  } catch (error) {
                                    toast.error('Failed to delete document');
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500 mb-2">No documents uploaded yet</p>
                        <p className="text-sm text-slate-400">Upload ID proofs, certificates, and other documents here</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-4">
                <Card data-testid="timeline-card">
                  <CardContent className="p-6">
                    {/* Add Timeline Entry Button */}
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Candidate Journey</h3>
                        <p className="text-sm text-slate-500">Status changes, profile views, and all activities</p>
                      </div>
                      <Button onClick={() => setAddTimelineOpen(true)} size="sm">
                        <Clock className="w-4 h-4 mr-2" />
                        Add Entry
                      </Button>
                    </div>

                    {timelineLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : timeline.length > 0 ? (
                      <div className="relative">
                        {/* Vertical Timeline Line */}
                        <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-300 via-slate-200 to-slate-100" />

                        <div className="space-y-4">
                          {timeline.map((entry, idx) => {
                            const isProfileView = entry.entry_type === 'profile_view';
                            const isAIAnalysis = entry.entry_type === 'ai_analysis';
                            const statusInfo = !isProfileView && !isAIAnalysis ? (TIMELINE_STATUSES.find(s => s.value === entry.status) || { label: entry.status, color: 'bg-slate-100 text-slate-700' }) : null;
                            
                            // Determine icon and color based on entry type
                            let dotColor = 'bg-blue-500';
                            let dotIcon = null;
                            
                            if (isAIAnalysis) {
                              dotColor = 'bg-emerald-500';
                              dotIcon = <Sparkles className="w-2.5 h-2.5 text-white" />;
                            } else if (isProfileView) {
                              if (entry.action === 'view_phone') {
                                dotColor = 'bg-amber-500';
                                dotIcon = <Phone className="w-2.5 h-2.5 text-white" />;
                              } else if (entry.action === 'view_email') {
                                dotColor = 'bg-purple-500';
                                dotIcon = <Mail className="w-2.5 h-2.5 text-white" />;
                              } else if (entry.action === 'view_resume') {
                                dotColor = 'bg-cyan-500';
                                dotIcon = <FileText className="w-2.5 h-2.5 text-white" />;
                              } else {
                                dotColor = 'bg-slate-400';
                                dotIcon = <Eye className="w-2.5 h-2.5 text-white" />;
                              }
                            } else {
                              if (entry.status === 'hired') dotColor = 'bg-green-500';
                              else if (entry.status === 'rejected') dotColor = 'bg-red-500';
                              else if (entry.status === 'offered') dotColor = 'bg-emerald-500';
                              else if (entry.status === 'interviewed') dotColor = 'bg-indigo-500';
                              else if (entry.status === 'shortlisted') dotColor = 'bg-yellow-500';
                            }
                            
                            return (
                              <div key={entry.id || idx} className="relative pl-12">
                                {/* Timeline Dot with Icon */}
                                <div className={`absolute left-1.5 w-7 h-7 rounded-full border-4 border-white shadow-md flex items-center justify-center ${dotColor}`}>
                                  {dotIcon}
                                </div>

                                {/* Entry Card */}
                                <div className={`rounded-xl p-4 border transition-all hover:shadow-md ${
                                  isAIAnalysis
                                    ? 'bg-emerald-50 border-emerald-200'
                                    : isProfileView 
                                    ? 'bg-slate-50 border-slate-200' 
                                    : 'bg-white border-slate-200 shadow-sm'
                                }`}>
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                      {/* Entry Header */}
                                      {isAIAnalysis ? (
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-200 text-emerald-800">
                                            <Sparkles className="w-3 h-3" />
                                            AI Analysis Generated
                                          </span>
                                          <span className="text-xs text-slate-500">
                                            by <span className="font-medium text-slate-700">{entry.performed_by_name}</span>
                                          </span>
                                        </div>
                                      ) : isProfileView ? (
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                                            <Eye className="w-3 h-3" />
                                            {entry.action === 'view_phone' && 'Viewed Phone'}
                                            {entry.action === 'view_email' && 'Viewed Email'}
                                            {entry.action === 'view_resume' && 'Viewed Resume'}
                                            {!['view_phone', 'view_email', 'view_resume'].includes(entry.action) && 'Profile Accessed'}
                                          </span>
                                          <span className="text-xs text-slate-500">
                                            by <span className="font-medium text-slate-700">{entry.performed_by_name}</span>
                                            {entry.performed_by_role && (
                                              <span className="text-slate-400"> ({entry.performed_by_role})</span>
                                            )}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${statusInfo?.color}`}>
                                          {statusInfo?.label}
                                        </span>
                                      )}
                                      
                                      {/* Company & Job Info for Status Changes */}
                                      {!isProfileView && !isAIAnalysis && entry.company_name && (
                                        <div className="flex items-center gap-2 mt-2 text-slate-700">
                                          <Building2 className="w-4 h-4 text-slate-400" />
                                          <span className="font-medium">{entry.company_name}</span>
                                        </div>
                                      )}
                                      
                                      {!isProfileView && !isAIAnalysis && entry.job_title && (
                                        <div className="flex items-center gap-2 mt-1 text-slate-600 text-sm">
                                          <Briefcase className="w-4 h-4 text-slate-400" />
                                          {entry.job_title}
                                        </div>
                                      )}
                                      
                                      {/* Call Status for Phone Views */}
                                      {isProfileView && entry.call_status && (
                                        <div className="flex items-center gap-2 mt-2">
                                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            entry.call_status === 'connected' ? 'bg-green-100 text-green-700' :
                                            entry.call_status === 'not_answered' ? 'bg-amber-100 text-amber-700' :
                                            entry.call_status === 'busy' ? 'bg-red-100 text-red-700' :
                                            entry.call_status === 'wrong_number' ? 'bg-red-100 text-red-700' :
                                            'bg-slate-100 text-slate-700'
                                          }`}>
                                            <PhoneCall className="w-3 h-3" />
                                            {entry.call_status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Notes */}
                                      {entry.notes && (
                                        <p className="mt-2 text-sm text-slate-600 bg-white/50 p-2 rounded border border-slate-100 italic">
                                          "{entry.notes}"
                                        </p>
                                      )}
                                    </div>
                                    
                                    {/* Timestamp */}
                                    <div className="text-right text-xs text-slate-400 flex-shrink-0 whitespace-nowrap">
                                      <div className="font-medium text-slate-500">
                                        {new Date(entry.created_at).toLocaleDateString('en-US', { 
                                          month: 'short', 
                                          day: 'numeric',
                                          year: new Date(entry.created_at).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                                        })}
                                      </div>
                                      <div>{new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                                      {!isProfileView && (
                                        <div className="mt-1 text-slate-400">by {entry.performed_by_name}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-lg font-medium">No timeline entries yet</p>
                        <p className="text-sm">Track this candidate's journey through the hiring process</p>
                        <Button onClick={() => setAddTimelineOpen(true)} className="mt-4" variant="outline">
                          <Clock className="w-4 h-4 mr-2" />
                          Add First Entry
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notes" className="mt-4">
                <Card data-testid="notes-card">
                  <CardContent className="p-6">
                    {/* Add New Note */}
                    <div className="mb-6">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <Textarea
                            placeholder="Add a note about this candidate... (e.g., availability, interview feedback, company submissions)"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            rows={3}
                            className="resize-none"
                            data-testid="new-note-input"
                          />
                        </div>
                        <Button
                          onClick={handleAddNote}
                          disabled={addingNote || !newNote.trim()}
                          data-testid="add-note-btn"
                        >
                          {addingNote ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <MessageSquarePlus className="w-4 h-4 mr-2" />
                              Add Note
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Notes History */}
                    <div className="border-t border-slate-100 pt-6">
                      <h4 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <StickyNote className="w-4 h-4" />
                        Notes History
                      </h4>
                      
                      {candidate.notes_history?.length > 0 ? (
                        <div className="space-y-4">
                          {[...candidate.notes_history].reverse().map((note) => (
                            <div
                              key={note.id}
                              className="relative p-4 bg-slate-50 rounded-lg border border-slate-100 group"
                              data-testid={`note-${note.id}`}
                            >
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="absolute top-3 right-3 p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-note-${note.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              
                              <p className="text-sm text-slate-700 whitespace-pre-wrap pr-8">
                                {note.note}
                              </p>
                              
                              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {note.added_by_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatNoteDate(note.added_at)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-8">
                          No notes yet. Add notes to track candidate progress, interview feedback, or company submissions.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Verifications Tab */}
              <TabsContent value="verifications" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-violet-100 rounded-lg">
                          <UserCheck className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800">Background Verifications</h3>
                          <p className="text-sm text-slate-500">Employment verification requests sent to previous employers</p>
                        </div>
                      </div>
                    </div>

                    {verificationsLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : verifications.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No verifications sent yet</p>
                        <p className="text-sm mt-1">Use the "Background Verify" button to send verification requests</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {verifications.map((v) => {
                          const daysSinceSent = v.email_sent_at 
                            ? Math.floor((new Date() - new Date(v.email_sent_at)) / (1000 * 60 * 60 * 24))
                            : null;
                          const needsResend = (v.status === 'pending' || v.status === 'email_sent') && daysSinceSent >= 7;
                          
                          return (
                            <div key={v.id} className="border rounded-lg p-4 hover:bg-slate-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{v.company_name}</span>
                                  </div>
                                  <p className="text-sm text-slate-600">
                                    Contact: {v.contact_name} ({v.contact_email})
                                  </p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    {v.candidate_designation && `Position: ${v.candidate_designation}`}
                                    {v.candidate_tenure_from && ` | Tenure: ${v.candidate_tenure_from} - ${v.candidate_tenure_to || 'Present'}`}
                                  </p>
                                  {v.email_sent_at && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      Sent: {new Date(v.email_sent_at).toLocaleDateString()} 
                                      {daysSinceSent > 0 && ` (${daysSinceSent} days ago)`}
                                      {v.email_count > 1 && ` • Sent ${v.email_count} times`}
                                    </p>
                                  )}
                                  {v.verified_at && (
                                    <p className="text-xs text-green-600 mt-1">
                                      <CheckCircle className="w-3 h-3 inline mr-1" />
                                      Verified on {new Date(v.verified_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                  {/* Status Dropdown */}
                                  <Select 
                                    value={v.status} 
                                    onValueChange={async (newStatus) => {
                                      try {
                                        const token = localStorage.getItem('ats_token');
                                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/background-verification/${v.id}/status`, {
                                          method: 'PUT',
                                          headers: { 
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${token}` 
                                          },
                                          body: JSON.stringify({ status: newStatus })
                                        });
                                        if (res.ok) {
                                          toast.success(`Status updated to ${newStatus}`);
                                          loadVerifications();
                                          loadTimeline();
                                        } else {
                                          toast.error('Failed to update status');
                                        }
                                      } catch (e) {
                                        toast.error('Failed to update status');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className={`w-32 h-8 text-xs ${
                                      v.status === 'verified' ? 'bg-green-100 border-green-300' :
                                      v.status === 'on_hold' ? 'bg-amber-100 border-amber-300' :
                                      v.status === 'not_verified' ? 'bg-red-100 border-red-300' :
                                      'bg-blue-100 border-blue-300'
                                    }`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="email_sent">Email Sent</SelectItem>
                                      <SelectItem value="verified">Verified</SelectItem>
                                      <SelectItem value="not_verified">Not Verified</SelectItem>
                                      <SelectItem value="on_hold">On Hold</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex gap-1">
                                    {(needsResend || !v.email_sent_at) && v.status !== 'verified' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        onClick={async () => {
                                          try {
                                            const token = localStorage.getItem('ats_token');
                                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/background-verification/${v.id}/send-email`, {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` }
                                            });
                                            if (res.ok) {
                                              toast.success('Verification email sent');
                                              loadVerifications();
                                            } else {
                                              toast.error('Failed to send email');
                                            }
                                          } catch (e) {
                                            toast.error('Failed to send email');
                                          }
                                        }}
                                      >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {v.email_sent_at ? 'Resend' : 'Send'}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Custom Fields Tab - AI Extracted Data */}
              {candidate.custom_fields && Object.keys(candidate.custom_fields).length > 0 && (
                <TabsContent value="custom_fields" className="mt-4">
                  <Card data-testid="custom-fields-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Zap className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800">AI-Extracted Data</h3>
                          <p className="text-sm text-slate-500">Additional information extracted by AI during resume parsing</p>
                        </div>
                      </div>

                      <div className="grid gap-4">
                        {Object.entries(candidate.custom_fields).map(([key, value]) => {
                          // Format the key for display
                          const formattedKey = key
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase());
                          
                          // Handle different value types
                          const renderValue = (val) => {
                            if (val === null || val === undefined || val === '') {
                              return <span className="text-slate-400 italic">Not specified</span>;
                            }
                            if (Array.isArray(val)) {
                              return (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {val.map((item, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-sm">
                                      {String(item)}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            if (typeof val === 'object') {
                              return (
                                <pre className="mt-1 p-2 bg-slate-50 rounded text-xs overflow-auto max-h-32">
                                  {JSON.stringify(val, null, 2)}
                                </pre>
                              );
                            }
                            if (typeof val === 'boolean') {
                              return (
                                <span className={`px-2 py-0.5 rounded text-sm ${val ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {val ? 'Yes' : 'No'}
                                </span>
                              );
                            }
                            return <span className="text-slate-700">{String(val)}</span>;
                          };

                          return (
                            <div 
                              key={key} 
                              className="p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border border-slate-200"
                              data-testid={`custom-field-${key}`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
                                    <Bot className="w-4 h-4 text-amber-500" />
                                    {formattedKey}
                                  </h4>
                                  <div className="mt-1.5">
                                    {renderValue(value)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-6 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-700 flex items-start gap-2">
                          <Bot className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            These fields were automatically extracted by AI during resume parsing. 
                            They represent additional data points that don't fit into the standard candidate profile.
                          </span>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Unlock History Tab */}
              <TabsContent value="unlocks" className="mt-4">
                <CandidateUnlocksTab candidateId={candidate.id} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Compensation */}
            <Card data-testid="compensation-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Compensation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {candidate.current_salary && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Current</span>
                    <span className="font-medium">{candidate.current_salary}</span>
                  </div>
                )}
                {candidate.expected_salary && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Expected</span>
                    <span className="font-medium">{candidate.expected_salary}</span>
                  </div>
                )}
                {candidate.notice_period_days !== null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Notice</span>
                    <span className="font-medium">
                      {candidate.notice_period_days === 'Immediate' || candidate.notice_period_days === 0
                        ? 'Immediate'
                        : `${candidate.notice_period_days} days`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Matching Jobs */}
            <Card data-testid="matches-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-500" />
                  Matching Jobs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {matches.length > 0 ? (
                  <div className="space-y-3">
                    {matches.slice(0, 5).map((match) => (
                      <Link
                        key={match.id}
                        to={`/ats/matches/${match.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`job-match-${match.id}`}
                      >
                        <ScoreRing score={match.overall_score} size="sm" showLabel={false} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {match.job?.job_title || 'Unknown Job'}
                          </p>
                          <p className="text-xs text-slate-500">{match.recommendation_level}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">No matches yet</p>
                )}
              </CardContent>
            </Card>

            {/* Meta Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Code</span>
                  <span className="font-mono">{candidate.candidate_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Parsed by</span>
                  <span className="capitalize">{candidate.ai_provider_used || 'Manual'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Added by</span>
                  <span>{candidate.uploaded_by_name || 'System'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Power Match Scores */}
            {powerMatchData?.latest && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-500" />
                    Power Match Scores
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Job Matched Against */}
                  <div className="p-3 bg-white rounded-lg border border-purple-100">
                    <div className="text-xs text-slate-500 mb-1">Matched Against</div>
                    <div className="font-semibold text-slate-800 truncate">{powerMatchData.latest.job_title}</div>
                  </div>

                  {/* All Provider Scores */}
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">AI Provider Scores</div>
                    {Object.entries(powerMatchData.latest.provider_results || {}).map(([provider, result]) => {
                      const isSelected = provider === powerMatchData.latest.best_provider;
                      const score = result.score || 0;
                      return (
                        <div 
                          key={provider} 
                          className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                            isSelected 
                              ? 'bg-purple-100 border-2 border-purple-400' 
                              : 'bg-slate-50 border border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected && <CheckCircle className="w-4 h-4 text-purple-600" />}
                            <span className={`text-sm font-medium capitalize ${isSelected ? 'text-purple-800' : 'text-slate-700'}`}>
                              {provider}
                            </span>
                          </div>
                          <div className={`text-lg font-bold ${
                            score >= 80 ? 'text-green-600' :
                            score >= 60 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {score}%
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Average Score */}
                  <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                    <span className="text-sm font-medium text-slate-600">Average Score</span>
                    <span className="text-xl font-bold text-slate-800">{powerMatchData.latest.average_score?.toFixed(1) || 0}%</span>
                  </div>

                  {/* Last Matched Timestamp */}
                  <div className="pt-2 border-t border-purple-100">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Last matched:</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-700">
                      {new Date(powerMatchData.latest.created_at).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                      {' at '}
                      {new Date(powerMatchData.latest.created_at).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      by {powerMatchData.latest.created_by_name}
                    </div>
                  </div>

                  {/* Total Matches Count */}
                  {powerMatchData.total > 1 && (
                    <div className="text-xs text-center text-slate-500 pt-2">
                      {powerMatchData.total} total Power Match{powerMatchData.total > 1 ? 'es' : ''} recorded
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Resume Modal */}
      <Dialog open={resumeOpen} onOpenChange={setResumeOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Original Resume - {candidate.name}
              {candidate.original_file_name && (
                <span className="text-sm font-normal text-slate-500">
                  ({candidate.original_file_name})
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100 rounded-lg">
            {cvLoading ? (
              <div className="flex items-center justify-center h-[70vh]">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : candidate.original_file_path && originalCvUrl ? (
              candidate.original_file_name?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={originalCvUrl}
                  className="w-full h-[70vh] border-0"
                  title="Original CV"
                />
              ) : candidate.original_file_name?.toLowerCase().endsWith('.txt') || 
                 candidate.original_file_name?.toLowerCase().endsWith('.html') ? (
                <iframe
                  src={originalCvUrl}
                  className="w-full h-[70vh] border-0 bg-white"
                  title="Original CV"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[70vh] gap-4">
                  <FileText className="w-16 h-16 text-slate-400" />
                  <p className="text-slate-600">
                    This file type ({candidate.original_file_name?.split('.').pop()?.toUpperCase()}) cannot be previewed directly.
                  </p>
                  <a href={originalCvUrl} download={candidate.original_file_name}>
                    <Button>
                      <Download className="w-4 h-4 mr-2" />
                      Download Original CV
                    </Button>
                  </a>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-[70vh] gap-4 text-slate-500">
                <FileText className="w-16 h-16 text-slate-300" />
                <p>Original CV file not available</p>
                <p className="text-sm">Showing parsed text instead:</p>
                <div className="max-w-2xl max-h-[50vh] overflow-y-auto p-4 bg-white rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm font-mono text-slate-700 leading-relaxed">
                    {candidate.cv_text || 'No resume text available'}
                  </pre>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {candidate.original_file_path && originalCvUrl && (
                <a
                  href={originalCvUrl}
                  download={candidate.original_file_name}
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Download Original
                </a>
              )}
            </div>
            <Button variant="outline" onClick={() => setResumeOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Power Match Modal */}
      <Dialog open={powerModeOpen} onOpenChange={setPowerModeOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              Power Match - Multi-AI Matching
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
            <p className="text-sm text-slate-600">
              Score this candidate against one or more jobs using multiple AI providers simultaneously.
            </p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Select Jobs to Match Against</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    if (selectedJobIds.length === jobs.length) {
                      setSelectedJobIds([]);
                    } else {
                      setSelectedJobIds(jobs.map(j => j.id));
                    }
                  }}
                  className="text-xs"
                >
                  {selectedJobIds.length === jobs.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1">
                {jobs.map((job) => {
                  const existingScore = jobMatchScores[job.id];
                  return (
                    <label
                      key={job.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all hover:bg-slate-50 ${
                        selectedJobIds.includes(job.id) ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <Checkbox
                        checked={selectedJobIds.includes(job.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedJobIds([...selectedJobIds, job.id]);
                          } else {
                            setSelectedJobIds(selectedJobIds.filter(id => id !== job.id));
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{job.job_title}</p>
                        {job.company_name && (
                          <p className="text-xs text-slate-500 truncate">{job.company_name}</p>
                        )}
                      </div>
                      {/* Match Score Circle */}
                      <div className="flex-shrink-0">
                        {existingScore ? (
                          <div className="relative w-12 h-12">
                            <svg className="transform -rotate-90 w-12 h-12">
                              <circle
                                cx="24" cy="24" r="20"
                                fill="none"
                                stroke="#e5e7eb"
                                strokeWidth="4"
                              />
                              <circle
                                cx="24" cy="24" r="20"
                                fill="none"
                                stroke={existingScore >= 70 ? '#22c55e' : existingScore >= 50 ? '#f59e0b' : '#ef4444'}
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeDasharray={`${(existingScore / 100) * 125.6} 125.6`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className={`text-xs font-bold ${
                                existingScore >= 70 ? 'text-green-600' : 
                                existingScore >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {Math.round(existingScore)}%
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-full border-2 border-dashed border-slate-200 flex items-center justify-center">
                            <span className="text-xs text-slate-400">New</span>
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
                {jobs.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No open jobs available</p>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {selectedJobIds.length} job{selectedJobIds.length !== 1 ? 's' : ''} selected
                {Object.keys(jobMatchScores).length > 0 && (
                  <span className="ml-2">• {Object.keys(jobMatchScores).length} already matched</span>
                )}
              </p>
            </div>

            <div>
              <Label>Select AI Providers</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {[
                  { id: 'anthropic', label: 'Claude (Anthropic)', color: 'bg-orange-100 text-orange-700' },
                  { id: 'openai', label: 'GPT-4o (OpenAI)', color: 'bg-green-100 text-green-700' },
                  { id: 'gemini', label: 'Gemini (Google)', color: 'bg-blue-100 text-blue-700' },
                ].map((provider) => (
                  <label
                    key={provider.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                      selectedProviders.includes(provider.id)
                        ? provider.color + ' ring-2 ring-offset-1'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    <Checkbox
                      checked={selectedProviders.includes(provider.id)}
                      onCheckedChange={() => toggleProvider(provider.id)}
                    />
                    <span className="text-sm font-medium">{provider.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Select all 3 for comprehensive analysis. API keys must be configured in Settings.
              </p>
            </div>

            {/* Results */}
            {powerLoading && (
              <div className="flex flex-col items-center justify-center py-8 gap-4">
                <CircularTimer 
                  duration={selectedJobIds.length * selectedProviders.length * 12} 
                  size={100}
                  strokeWidth={8}
                  isRunning={powerLoading}
                  label={`Analyzing ${selectedJobIds.length} job${selectedJobIds.length > 1 ? 's' : ''} with ${selectedProviders.length} AI${selectedProviders.length > 1 ? 's' : ''}...`}
                />
              </div>
            )}

            {!powerLoading && powerResults && (
              <div className="mt-6 space-y-4">
                {/* Multi-Job Results */}
                {powerResults.multi_job ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-600">Jobs Matched</p>
                          <p className="text-4xl font-bold text-slate-900">{powerResults.total_jobs}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">Candidate</p>
                          <p className="font-semibold text-lg">{powerResults.candidate_name}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Individual Job Results */}
                    {powerResults.results.map((jobResult, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        <div className="p-3 bg-slate-50 border-b flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">{jobResult.job_title}</p>
                            <p className="text-xs text-slate-500">{jobResult.company_name}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-xs text-slate-500">Avg Score</p>
                              <p className={`text-2xl font-bold ${
                                jobResult.average_score >= 70 ? 'text-green-600' :
                                jobResult.average_score >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {Math.round(jobResult.average_score)}
                              </p>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleSavePowerMatch(jobResult)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                        <div className="p-3 grid grid-cols-3 gap-2">
                          {Object.entries(jobResult.results).map(([provider, result]) => (
                            <div key={provider} className={`p-2 rounded text-center ${
                              result.error ? 'bg-red-50' : 'bg-slate-50'
                            }`}>
                              <p className="text-xs text-slate-500 capitalize">{provider}</p>
                              <p className={`text-lg font-bold ${
                                result.error ? 'text-red-400' :
                                result.score >= 70 ? 'text-green-600' :
                                result.score >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {result.error ? 'Err' : result.score}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Single Job Results - Original Layout */}
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-600">Average Score</p>
                          <p className="text-4xl font-bold text-slate-900">
                            {powerResults.average_score !== null ? Math.round(powerResults.average_score) : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">Candidate</p>
                          <p className="font-semibold text-lg">{powerResults.candidate_name}</p>
                          <p className="text-sm text-slate-500">→ {powerResults.job_title}</p>
                        </div>
                      </div>
                      {powerResults.best_provider && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-xs text-purple-600 font-medium">
                            🏆 Best Match: {powerResults.best_provider.toUpperCase()} scored {powerResults.best_score}
                          </p>
                        </div>
                      )}
                    </div>

                {/* Individual Provider Results */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(powerResults.results).map(([provider, result]) => {
                    const isBest = provider === powerResults.best_provider;
                    return (
                      <div
                        key={provider}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          result.error 
                            ? 'bg-red-50 border-red-200' 
                            : isBest 
                              ? 'bg-green-50 border-green-400 ring-2 ring-green-200 ring-offset-2' 
                              : 'bg-white border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isBest && <span className="text-green-500">🏆</span>}
                            <span className="font-medium capitalize">{provider}</span>
                          </div>
                          {result.score !== null && (
                            <span className={`text-xl font-bold ${
                              result.score >= 70 ? 'text-green-600' :
                              result.score >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {result.score}
                            </span>
                          )}
                        </div>
                        {isBest && (
                          <div className="mb-2 text-xs text-green-600 font-medium">
                            ✓ Recommended - Highest Score
                          </div>
                        )}
                        {result.error ? (
                          <p className="text-xs text-red-600">{result.error}</p>
                        ) : (
                          <>
                            {result.reasoning && (
                              <p className="text-xs text-slate-600 mb-2 line-clamp-3">{result.reasoning}</p>
                            )}
                            {result.strengths?.length > 0 && (
                              <div className="mb-1">
                                <span className="text-xs font-medium text-green-600">Strengths: </span>
                                <span className="text-xs text-slate-600">{result.strengths.slice(0, 3).join(', ')}</span>
                              </div>
                            )}
                            {result.gaps?.length > 0 && (
                              <div>
                                <span className="text-xs font-medium text-amber-600">Gaps: </span>
                                <span className="text-xs text-slate-600">{result.gaps.slice(0, 3).join(', ')}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => { setPowerModeOpen(false); setPowerResults(null); setSelectedJobIds([]); }}>
              Close
            </Button>
            {powerResults && !powerResults.multi_job && (
              <Button
                onClick={() => handleSavePowerMatch()}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Power Match
              </Button>
            )}
            {powerResults?.multi_job && (
              <Button
                onClick={handleSaveAllPowerMatches}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save All ({powerResults.total_jobs}) Matches
              </Button>
            )}
            <Button
              onClick={handlePowerMatch}
              disabled={powerLoading || selectedJobIds.length === 0 || selectedProviders.length === 0}
              className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
            >
              {powerLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  {powerResults ? 'Run Again' : `Run Power Match${selectedJobIds.length > 1 ? ` (${selectedJobIds.length} jobs)` : ''}`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Power Parse Modal */}
      <Dialog open={powerParseOpen} onOpenChange={setPowerParseOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-amber-500" />
              Power Parse - Multi-AI Resume Parsing
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
            <p className="text-sm text-slate-600">
              Re-parse this candidate's resume using multiple AI providers and compare the extracted data. 
              Select the result with the best accuracy to update the candidate profile.
            </p>

            <div>
              <Label>Select AI Providers</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {[
                  { id: 'anthropic', label: 'Claude (Anthropic)', color: 'bg-orange-100 text-orange-700' },
                  { id: 'openai', label: 'GPT-4o (OpenAI)', color: 'bg-green-100 text-green-700' },
                  { id: 'gemini', label: 'Gemini (Google)', color: 'bg-blue-100 text-blue-700' },
                ].map((provider) => (
                  <label
                    key={provider.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                      parseProviders.includes(provider.id)
                        ? provider.color + ' ring-2 ring-offset-1'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    <Checkbox
                      checked={parseProviders.includes(provider.id)}
                      onCheckedChange={() => toggleParseProvider(provider.id)}
                    />
                    <span className="text-sm font-medium">{provider.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                API keys must be configured in Settings. Higher completeness score = more data extracted.
              </p>
            </div>

            {/* Results */}
            {parseLoading && (
              <div className="flex flex-col items-center justify-center py-8 gap-4">
                <CircularTimer 
                  duration={parseProviders.length * 20} 
                  size={100}
                  strokeWidth={8}
                  isRunning={parseLoading}
                  label={`Parsing with ${parseProviders.length} AI${parseProviders.length > 1 ? 's' : ''}...`}
                />
                <p className="text-sm text-slate-500">
                  Running {parseProviders.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}
                </p>
              </div>
            )}

            {!parseLoading && parseResults && (
              <div className="mt-6 space-y-4">
                {/* Summary */}
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Best Completeness Score</p>
                      <p className="text-4xl font-bold text-slate-900">
                        {parseResults.best_score !== null ? `${parseResults.best_score}%` : 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Candidate</p>
                      <p className="font-semibold text-lg">{parseResults.candidate_name}</p>
                    </div>
                  </div>
                  {parseResults.best_provider && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-amber-600 font-medium">
                        🏆 Best Result: {parseResults.best_provider.toUpperCase()} with {parseResults.best_score}% completeness
                      </p>
                    </div>
                  )}
                </div>

                {/* Provider Results */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(parseResults.results).map(([provider, result]) => {
                    const isBest = provider === parseResults.best_provider;
                    const isSelected = selectedParseResult?.provider === provider;
                    const completeness = result.completeness;
                    
                    return (
                      <div
                        key={provider}
                        onClick={() => !result.error && setSelectedParseResult({ 
                          provider, 
                          data: result.parsed_data,
                          customFields: result.custom_fields_detected || {}
                        })}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          result.error 
                            ? 'bg-red-50 border-red-200 cursor-not-allowed'
                            : isSelected
                              ? 'bg-amber-50 border-amber-500 ring-2 ring-amber-300 ring-offset-2'
                              : isBest 
                                ? 'bg-green-50 border-green-400 hover:border-green-500' 
                                : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isBest && <span className="text-green-500">🏆</span>}
                            {isSelected && <CheckCircle className="w-4 h-4 text-amber-500" />}
                            <span className="font-medium capitalize">{provider}</span>
                          </div>
                          {completeness && (
                            <span className={`text-xl font-bold ${
                              completeness.score >= 70 ? 'text-green-600' :
                              completeness.score >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {completeness.score}%
                            </span>
                          )}
                        </div>
                        
                        {isBest && !isSelected && (
                          <div className="mb-2 text-xs text-green-600 font-medium">
                            ✓ Highest Completeness
                          </div>
                        )}
                        
                        {isSelected && (
                          <div className="mb-2 text-xs text-amber-600 font-medium">
                            ✓ Selected to Apply
                          </div>
                        )}
                        
                        {result.error ? (
                          <p className="text-xs text-red-600">{result.error}</p>
                        ) : completeness && (
                          <>
                            <div className="text-xs text-slate-600 mb-2">
                              <span className="font-medium">{completeness.fields_found}</span> of {completeness.fields_total} fields extracted
                            </div>
                            
                            {/* Progress bar */}
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
                              <div 
                                className={`h-full transition-all ${
                                  completeness.score >= 70 ? 'bg-green-500' :
                                  completeness.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${completeness.score}%` }}
                              />
                            </div>
                            
                            {/* Sample extracted data */}
                            {result.parsed_data && (
                              <div className="space-y-1 text-xs">
                                {result.parsed_data.name && (
                                  <div><span className="text-slate-500">Name:</span> {result.parsed_data.name}</div>
                                )}
                                {result.parsed_data.email && (
                                  <div><span className="text-slate-500">Email:</span> {result.parsed_data.email}</div>
                                )}
                                {result.parsed_data.total_years_experience && (
                                  <div><span className="text-slate-500">Experience:</span> {result.parsed_data.total_years_experience} years</div>
                                )}
                                {result.parsed_data.technical_skills?.length > 0 && (
                                  <div><span className="text-slate-500">Skills:</span> {result.parsed_data.technical_skills.slice(0, 3).join(', ')}...</div>
                                )}
                              </div>
                            )}
                            
                            {/* Missing fields hint */}
                            {completeness.fields_missing_list?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-100">
                                <p className="text-xs text-slate-400">
                                  Missing: {completeness.fields_missing_list.slice(0, 4).join(', ')}
                                  {completeness.fields_missing_list.length > 4 && '...'}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selection hint */}
                <p className="text-xs text-center text-slate-500">
                  Click on a result card to select it for applying to the candidate profile.
                  You can choose any result, not just the highest scoring one.
                </p>

                {/* Custom Fields Notice */}
                {parseResults?.has_custom_fields && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                      <span>📦</span>
                      Additional data detected! 
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      The AI found extra information not in the standard schema. 
                      You'll be asked which fields to save when applying.
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.keys(parseResults.all_custom_fields || {}).slice(0, 5).map(field => (
                        <span key={field} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          {field.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {Object.keys(parseResults.all_custom_fields || {}).length > 5 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          +{Object.keys(parseResults.all_custom_fields).length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => { setPowerParseOpen(false); setParseResults(null); setSelectedParseResult(null); }}>
              Close
            </Button>
            {selectedParseResult && (
              <Button
                onClick={handleApplyPowerParse}
                disabled={applyingParse}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {applyingParse ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Apply {selectedParseResult.provider} Result
              </Button>
            )}
            <Button
              onClick={handlePowerParse}
              disabled={parseLoading || parseProviders.length === 0}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {parseLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  {parseResults ? 'Parse Again' : 'Run Power Parse'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Fields Selection Modal */}
      <Dialog open={showCustomFieldsModal} onOpenChange={setShowCustomFieldsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>📦</span>
              Additional Data Found
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              The AI extracted additional information that isn't in the standard candidate profile. 
              Select which fields you'd like to save as custom fields.
            </p>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedParseResult?.customFields && Object.entries(selectedParseResult.customFields).map(([fieldName, value]) => {
                const isSelected = selectedCustomFields[fieldName] !== undefined;
                const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                
                return (
                  <div
                    key={fieldName}
                    onClick={() => toggleCustomField(fieldName, value)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-blue-50 border-blue-400' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox checked={isSelected} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 capitalize">
                          {fieldName.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-slate-600 truncate">
                          {displayValue.length > 100 ? displayValue.substring(0, 100) + '...' : displayValue}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Selected fields will be saved to the candidate's custom fields and can be viewed/edited later.
              Field definitions will be saved for future use across all candidates.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCustomFieldsModal(false);
              setSelectedCustomFields({});
            }}>
              Skip All
            </Button>
            <Button
              onClick={() => {
                setShowCustomFieldsModal(false);
                handleApplyPowerParse();
              }}
              disabled={applyingParse}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {applyingParse ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Save {Object.keys(selectedCustomFields).length} Custom Fields & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Notes Modal */}
      {/* Access Notes Side Panel - slides from right so revealed data stays visible */}
      <Sheet open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" />
              Add Access Notes
            </SheetTitle>
          </SheetHeader>

          <div className="py-6 space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                ✓ {currentField?.charAt(0).toUpperCase() + currentField?.slice(1)} revealed successfully
              </p>
              <p className="text-xs text-green-600 mt-1">
                You can see the data on the left. Add notes below.
              </p>
            </div>

            {/* QR Code for Phone - Scan to Dial */}
            {currentField === 'phone' && revealedData.phone && (
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-800">Scan to Dial</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <QRCodeSVG 
                      value={`tel:${revealedData.phone.replace(/\s/g, '')}`}
                      size={100}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-bold text-slate-800">{revealedData.phone}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Scan this QR code with your phone camera to dial instantly
                    </p>
                    <a 
                      href={`tel:${revealedData.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <PhoneCall className="w-4 h-4" />
                      Click to call from this device
                    </a>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-slate-600">
              Please add notes about why you accessed this information.
            </p>

            {currentField === 'phone' && (
              <div className="space-y-2">
                <Label>Call Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CALL_STATUSES.map((status) => {
                    const Icon = status.icon;
                    return (
                      <button
                        key={status.value}
                        onClick={() => setCallStatus(status.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          callStatus === status.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${status.color}`} />
                        <span className="text-sm font-medium">{status.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes / Feedback</Label>
              <Textarea
                value={accessNotes}
                onChange={(e) => setAccessNotes(e.target.value)}
                placeholder={currentField === 'phone' 
                  ? "What did you discuss? Any follow-up required?"
                  : "Why did you access this information?"
                }
                rows={4}
              />
            </div>
          </div>

          <SheetFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setNotesModalOpen(false)} className="flex-1">
              Skip
            </Button>
            <Button onClick={handleSaveAccessNotes} disabled={savingNotes} className="flex-1">
              {savingNotes ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Notes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Add Timeline Entry Modal */}
      <Dialog open={addTimelineOpen} onOpenChange={setAddTimelineOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Add Timeline Entry
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={timelineEntry.status} onValueChange={(v) => setTimelineEntry({...timelineEntry, status: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINE_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      <span className={`px-2 py-0.5 rounded ${status.color}`}>{status.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Company</Label>
              <Select 
                value={timelineEntry.company_id} 
                onValueChange={(v) => {
                  const company = companies.find(c => c.id === v);
                  setTimelineEntry({
                    ...timelineEntry, 
                    company_id: v,
                    company_name: company?.company_name || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Job</Label>
              <Select 
                value={timelineEntry.job_id} 
                onValueChange={(v) => {
                  const job = jobs.find(j => j.id === v);
                  setTimelineEntry({
                    ...timelineEntry, 
                    job_id: v,
                    job_title: job?.job_title || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select job (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.job_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={timelineEntry.notes}
                onChange={(e) => setTimelineEntry({...timelineEntry, notes: e.target.value})}
                placeholder="Interview feedback, rejection reason, offer details..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTimelineOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTimelineEntry}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Dialog - v2 (.ics + Google Calendar + reminders); the gen-1 body posted
          title/start_time to a route that expects round_name/mode/scheduled_at and crashed the page */}
      <ScheduleInterviewDialog
        open={scheduleInterviewOpen}
        onOpenChange={setScheduleInterviewOpen}
        candidate={candidate}
        onSaved={() => { loadInterviews(); loadTimeline(); loadCandidate(); }}
      />

      {/* Background Verification Modal */}
      <Dialog open={verificationModalOpen} onOpenChange={setVerificationModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-violet-600" />
              Background Verification - {candidate.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Existing Verifications */}
            {verifications.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-600">Previous Verifications</h4>
                {verifications.map((v) => (
                  <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{v.company_name}</p>
                      <p className="text-sm text-slate-500">{v.contact_name} ({v.contact_email})</p>
                    </div>
                    <Badge className={
                      v.status === 'verified' ? 'bg-green-100 text-green-700' :
                      v.status === 'email_sent' ? 'bg-blue-100 text-blue-700' :
                      v.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {v.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* Pre-fill from work experience */}
            {candidate.work_experience?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-600">Quick Fill from Work History</h4>
                <div className="flex flex-wrap gap-2">
                  {candidate.work_experience.slice(0, 5).map((exp, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => setNewVerification({
                        ...newVerification,
                        company_name: exp.company_name || '',
                        candidate_designation: exp.designation || exp.title || '',
                        candidate_tenure_from: exp.start_date || '',
                        candidate_tenure_to: exp.end_date || exp.is_current ? 'Present' : ''
                      })}
                    >
                      {exp.company_name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={newVerification.company_name}
                  onChange={(e) => setNewVerification({ ...newVerification, company_name: e.target.value })}
                  placeholder="Previous employer name"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person Name</Label>
                <Input
                  value={newVerification.contact_name}
                  onChange={(e) => setNewVerification({ ...newVerification, contact_name: e.target.value })}
                  placeholder="HR Manager / Reporting Manager"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={newVerification.contact_email}
                  onChange={(e) => setNewVerification({ ...newVerification, contact_email: e.target.value })}
                  placeholder="hr@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={newVerification.contact_phone}
                  onChange={(e) => setNewVerification({ ...newVerification, contact_phone: e.target.value })}
                  placeholder="+91 99999 99999"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Designation</Label>
                <Input
                  value={newVerification.contact_designation}
                  onChange={(e) => setNewVerification({ ...newVerification, contact_designation: e.target.value })}
                  placeholder="HR Manager"
                />
              </div>
              <div className="space-y-2">
                <Label>Candidate's Designation</Label>
                <Input
                  value={newVerification.candidate_designation}
                  onChange={(e) => setNewVerification({ ...newVerification, candidate_designation: e.target.value })}
                  placeholder="Software Engineer"
                />
              </div>
              <div className="space-y-2">
                <Label>Tenure From</Label>
                <DatePicker
                  value={newVerification.candidate_tenure_from}
                  onChange={(date) => setNewVerification({ ...newVerification, candidate_tenure_from: date })}
                  placeholder="Start date"
                />
              </div>
              <div className="space-y-2">
                <Label>Tenure To</Label>
                <DatePicker
                  value={newVerification.candidate_tenure_to}
                  onChange={(date) => setNewVerification({ ...newVerification, candidate_tenure_to: date })}
                  placeholder="End date"
                />
              </div>
            </div>

            {/* Documents to attach */}
            {candidate.documents?.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-1">Documents to be attached:</p>
                <div className="flex flex-wrap gap-2">
                  {candidate.documents.map((doc) => (
                    <Badge key={doc.id} variant="outline" className="bg-white">
                      <FileText className="w-3 h-3 mr-1" />
                      {doc.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {!candidate.documents?.length && (
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                No documents uploaded. Please upload documents in the Documents tab first.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVerificationModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddVerification} 
              disabled={sendingVerification || !candidate.documents?.length}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {sendingVerification ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Verification Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Status to "{TIMELINE_STATUSES.find(s => s.value === pendingStatus)?.label || pendingStatus}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company (Optional)</Label>
              <Select value={statusCompanyId || "none"} onValueChange={(v) => setStatusCompanyId(v === "none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Add notes about this status change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModalOpen(false)}>Cancel</Button>
            <Button onClick={confirmStatusChange} disabled={savingStatus}>
              {savingStatus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel/Reschedule Interview Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Interview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>What would you like to do?</Label>
              <Select value={cancelAction} onValueChange={setCancelAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cancel">Cancel Interview</SelectItem>
                  <SelectItem value="reschedule">Reschedule Interview</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {cancelAction === 'cancel' && (
              <div className="space-y-2">
                <Label>Reason for Cancellation</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Candidate not available">Candidate not available</SelectItem>
                    <SelectItem value="Interviewer not available">Interviewer not available</SelectItem>
                    <SelectItem value="Position filled">Position filled</SelectItem>
                    <SelectItem value="Candidate withdrew">Candidate withdrew</SelectItem>
                    <SelectItem value="Company decision">Company decision</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {cancelAction === 'reschedule' && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <Calendar className="w-4 h-4 inline mr-1" />
                The current interview will be cancelled and you'll be prompted to select a new date and time.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>Back</Button>
            <Button 
              onClick={confirmCancelInterview}
              variant={cancelAction === 'cancel' ? 'destructive' : 'default'}
            >
              {cancelAction === 'cancel' ? 'Cancel Interview' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
